import { useQuery } from '@tanstack/react-query';
import { useSearch, Link } from '@tanstack/react-router';
import { getWeatherByCoordinates } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { LoadingState, SEO, ForecastPeriodCards, SevenDayForecast, HourlyForecastSection } from '@/components';
import { FaTint, FaWind, FaEye, FaThermometerHalf, FaCrosshairs } from 'react-icons/fa';
import { calcDewPoint } from '@/lib/weather';

const defaultHero = 'https://images.unsplash.com/photo-1516912481808-3406841bd33c?w=600&h=300&fit=crop';

export function WeatherCoordinatesPage() {
  const search = useSearch({ from: '/weather/coordinates' });
  const lat = parseFloat(search.lat as string);
  const lon = parseFloat(search.lon as string);
  const cityName = (search.city as string) || 'Current Location';

  const validCoords = !isNaN(lat) && !isNaN(lon);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.weather.byCoordinates(lat, lon),
    queryFn: () => getWeatherByCoordinates(lat, lon),
    enabled: validCoords,
  });

  if (!validCoords) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
        <div className="text-6xl mb-4">📍</div>
        <h2 className="text-2xl font-bold mb-2">Invalid Coordinates</h2>
        <p className="text-white/60 mb-6">
          Please provide valid latitude and longitude coordinates.
        </p>
        <Link
          to="/"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-info)] hover:bg-[var(--color-info)]/80 rounded-lg font-medium transition-colors"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading weather..." />;
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
        <div className="text-6xl mb-4">🌧️</div>
        <h2 className="text-2xl font-bold mb-2">Weather Unavailable</h2>
        <p className="text-white/60 mb-6">
          We couldn't fetch weather for these coordinates. They may be outside Michigan.
        </p>
        <Link
          to="/"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-info)] hover:bg-[var(--color-info)]/80 rounded-lg font-medium transition-colors"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  const { current, hourly, daily, timezone } = data;
  const dewPoint = calcDewPoint(current);

  const pageTitle = `${cityName}, MI - ${current.temperature ?? '--'}°F ${current.conditions}`;
  const pageDescription = `Current weather at ${cityName}, Michigan: ${current.conditions}, ${current.temperature ?? '--'}°F. Hourly and 7-day forecast for your location.`;

  return (
    <div className="space-y-4">
      <SEO
        title={pageTitle}
        description={pageDescription}
        path={`/weather/coordinates?lat=${lat}&lon=${lon}`}
      />
      {/* Main Layout: Weather Card + 7-Day Forecast side by side */}
      <div className="flex flex-col md:flex-row md:items-start gap-4">

        {/* Main Weather Card */}
        <div className="flex-1 relative rounded-2xl overflow-hidden h-[280px] md:h-[388px]">
          {/* Background Image */}
          <img
            src={defaultHero}
            alt={`${cityName}, Michigan - weather forecast background`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1a2d]/80 via-[#0d1a2d]/40 to-transparent" />

          {/* Content */}
          <div className="relative h-full p-5 flex flex-col">
            {/* Location Name */}
            <div className="flex items-center gap-3 mb-auto">
              <h1 className="text-xl font-bold uppercase tracking-wider">
                {cityName}, MI
              </h1>
              <FaCrosshairs className="text-green-400" title="GPS location" />
            </div>

            {/* Bottom Content */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              {/* Temperature Box */}
              <div className="stat-box rounded-xl p-4">
                <div className="text-5xl font-black leading-none mb-1">{current.temperature ?? '--'}°F</div>
                <p className="text-sm text-white/90">{current.conditions}</p>
                <p className="text-[#4ba3c3] text-sm">Feels like {current.feelsLike ?? '--'}°</p>
              </div>

              {/* Stats Grid - 2x2 */}
              <div className="grid grid-cols-2 gap-2 sm:ml-auto">
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Wind</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaWind className="text-xs text-white/60" /> {current.windSpeed ?? '--'} mph {current.windDirection}
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Humidity</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaTint className="text-xs text-white/60" /> {current.humidity}%
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Visibility</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaEye className="text-xs text-white/60" /> {current.visibility} mi
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Dew Point</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaThermometerHalf className="text-xs text-white/60" /> {dewPoint ?? '--'}°
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 7-Day Forecast - Right Column */}
        <SevenDayForecast daily={daily} timezone={timezone} />
      </div>

      {/* Forecast Period Cards */}
      <ForecastPeriodCards daily={daily} timezone={timezone} />

      {/* Hourly Forecast - Full Width Below */}
      <HourlyForecastSection hourly={hourly} locationName={cityName} timezone={timezone} />
    </div>
  );
}
